import React from "react"
import { useSelector } from "react-redux"
import { Card, CardBody, CardTitle, Col, Row } from "reactstrap"
import { useTranslation } from "react-i18next"
import { dountChartBgColor, dountChartHoverBgColor } from "../dashboardColors"
import { hasValidChartData } from "../dashboardChartOptions"
import DountChart from "pages/Chartsjs/dountchart"
import BarChart from "../barchart"
import TheoreticalTrainingGoalsDashboard from "../TheoreticalTrainingGoalsDashboard"
import IconifyIcon from "components/Shared/IconifyIcon"
import { layoutModeTypes } from "constants/layout"
import Colors from "constants/Colors"
import TheoreticalTrainingGoalsDashboardV2 from "./PracticalTrainingGoalsDashboard"
import SaudiNonSaudiSummaryCard from "./SaudiNonSaudiSummaryCard"
import ExperienceSummaryCard from "./ExpereinceSummaryCard"
import PracticalTrainingGoalsDashboard from "./PracticalTrainingGoalsDashboard"

const PracticalTrainingStatistics = ({ filters }) => {
  const { loading, dashboardData } = useSelector(state => state.Dashboard)
  const layoutMode = useSelector(state => state.Layout.layoutModeType)

  const {
    t,
    i18n: { language },
  } = useTranslation()

  const getChartData = (stats, labelKey) => ({
    labels:
      stats?.map(item =>
        language === "sa" ? item[`${labelKey}_ar`] : item[labelKey]
      ) || [],
    datasets: [
      {
        data: stats?.map(item => item.count) || [],
        backgroundColor: dountChartBgColor,
        hoverBackgroundColor: dountChartHoverBgColor,
        borderRadius: 7,
      },
    ],
  })

  const practicleTrainingOverviews = [
    {
      id: 1,
      title: "Present",
      count:
        dashboardData?.count_trainings_overview
          ?.total_completely_attended_participants,
    },
    {
      id: 3,
      title: "Absent",
      count: dashboardData?.count_trainings_overview?.total_absences || 0,
    },

    {
      id: 4,
      title: "Passed",
      count:
        dashboardData?.count_trainings_overview?.total_completed_participants ||
        0,
    },
    {
      id: 2,
      title: "Failed",
      count:
        dashboardData?.count_trainings_overview?.total_failed_participants || 0,
    },

    {
      id: 5,
      title: "Not Invited",
      count: dashboardData?.count_trainings_overview?.total_uninvited_count,
    },
  ]

  const dountChartOptions = {
    cutout: "80%",
    plugins: {
      legend: {
        onClick: null,
        position: "bottom",
        labels: {
          usePointStyle: true,
          boxWidth: 10,
          boxHeight: 10,
          padding: 5,
          font: { size: 12 },
          generateLabels: chart => {
            const { data } = chart
            return data.labels.map((label, i) => ({
              text: `${label} ${data.datasets[0].data[i].toLocaleString()}`, // Adds commas
              fillStyle: data.datasets[0].backgroundColor[i],
              fontColor:
                layoutMode === layoutModeTypes.LIGHT
                  ? Colors.black
                  : Colors.white,
              hidden: false,
              index: i,
              strokeStyle: data.datasets[0].backgroundColor[i],
              pointStyle: "rect",
            }))
          },
        },
      },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const dataset = tooltipItem.dataset.data
            const total = dataset.reduce((acc, num) => acc + num, 0)
            const value = dataset[tooltipItem.dataIndex]
            const percentage = ((value / total) * 100).toFixed(1)

            return [
              `${t("Total Count")}: ${value.toLocaleString()}`,
              `${t("Percentage")}: ${percentage}%`,
            ]
          },
        },
      },
      datalabels: {
        display: false,
      },
    },
  }

  const saudiNonSaudiChartData = {
    labels: [t("Saudi"), t("Non Saudi")],
    datasets: [
      {
        data: [
          dashboardData?.saudi_or_non_saudi_stats?.total_saudi_candidate || 0,
          dashboardData?.saudi_or_non_saudi_stats?.total_non_saudi_candidate ||
            0,
        ],
        backgroundColor: dountChartBgColor,
        hoverBackgroundColor: dountChartHoverBgColor,
        borderRadius: 7,
      },
    ],
  }
  const citiesChartData = getChartData(dashboardData?.city_stats, "city_name")
  const nationalitiesChartData = getChartData(
    dashboardData?.nationalities_stats,
    "nationality_name"
  )

  const chartConfigs = [
    { title: "Cities", data: citiesChartData },
    { title: "Nationalities", data: nationalitiesChartData },
  ]

  //   AGE:
  const data = {
    labels: dashboardData?.ages_stats?.map(a => a.age.toString()) || [],
    datasets: [
      {
        label: t("Total Count"),
        data: dashboardData?.ages_stats?.map(a => a.count) || [],
        backgroundColor: "#00778B",
      },
    ],
  }

  const options = {
    indexAxis: "y",
    plugins: {
      datalabels: {
        anchor: "end",
        align: "end",
        color: "#fff",
        formatter: (value, ctx) => {
          const total = ctx.dataset.data.reduce((sum, num) => sum + num, 0)
          const percentage = ((value / total) * 100).toFixed(1) + "%"
          return `${value} (${percentage})`
        },
      },
      legend: {
        onClick: null,
        labels: {
          color:
            layoutMode === layoutModeTypes.LIGHT ? Colors.black : Colors.white,
        },
      },
    },
    responsive: true,
    scales: {
      x: {
        grid: {
          color: "#f1f1f1",
          borderDash: [4, 4],
          lineWidth: 1,
        },
        ticks: {
          color:
            layoutMode === layoutModeTypes.LIGHT ? Colors.black : Colors.white,
        },
      },
      y: {
        grid: {
          color: "#f1f1f1",
          borderDash: [4, 4],
          lineWidth: 1,
        },
        ticks: {
          color:
            layoutMode === layoutModeTypes.LIGHT ? Colors.black : Colors.white,
        },
      },
    },
  }

  return (
    <>
      {loading ? (
        ""
      ) : (
        <>
          <Row data-page-section="true">
            <Col sm={12}>
              <Card>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <IconifyIcon
                    icon="grommet-icons:overview"
                    width={24}
                    height={24}
                    style={{
                      color:
                        layoutMode === layoutModeTypes.LIGHT
                          ? Colors.primary
                          : Colors.primaryLight,
                    }}
                  />
                  <CardTitle
                    style={{
                      color:
                        layoutMode === layoutModeTypes.LIGHT
                          ? Colors.black
                          : Colors.white,
                    }}
                    className="m-0"
                  >
                    {t("Overview")}
                  </CardTitle>
                </div>
                <p
                  className="mb-2"
                  style={{
                    color:
                      layoutMode === layoutModeTypes.LIGHT
                        ? Colors.mutedBlue
                        : Colors.white,
                  }}
                >
                  {t("General statistics on practical training")}
                </p>
                <CardBody
                  className="rounded-4"
                  style={{ border: "1px solid #DDE3EE" }}
                >
                  <div>
                    <Row className="mb-3 py-4">
                      <Col xs={12} md={6} className="text-center mb-3 mb-md-0">
                        <h3 className="fw-bold">
                          {t("Target Number Till Date")}
                        </h3>
                        <h2 className="fw-bold text-primary">
                          {dashboardData?.count_trainings_overview
                            ?.total_trainings_capacity || 0}
                        </h2>
                      </Col>
                      <Col xs={12} md={6} className="text-center">
                        <h3 className="fw-bold">{t("Enrolled Till Date")}</h3>
                        <h2 className="fw-bold text-primary">
                          {dashboardData?.count_trainings_overview
                            ?.total_booked_appointments || 0}
                        </h2>
                      </Col>
                    </Row>
                  </div>
                  <hr />
                  <Row className="py-4">
                    {practicleTrainingOverviews?.map(item => (
                      <Col key={item?.id} xs={6} md={2} className="text-center">
                        <p style={{ fontSize: "14px" }}>{t(item?.title)}</p>
                        <h4 style={{ fontSize: "16px" }} className="fw-bold">
                          {item?.count || 0}
                        </h4>
                      </Col>
                    ))}
                  </Row>
                </CardBody>
              </Card>
            </Col>
            <Col sm={12}>
              <div
                className="d-grid gap-4 mb-4"
                style={{
                  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                  overflowX: "hidden", // Prevent content from overflowing
                  width: "100%", // Ensure full container width
                }}
              >
                {chartConfigs?.map(({ title, data }) => (
                  <div
                    key={title}
                    className="rounded-4 pt-5"
                    style={{
                      border: "1px solid #DDE3EE",
                      minWidth: 0, // Important: Allows grid items to shrink
                    }}
                  >
                    {hasValidChartData(data) ? (
                      <div className="ltr">
                        <DountChart
                          data={data}
                          options={dountChartOptions}
                          label={t(title)}
                        />
                      </div>
                    ) : (
                      <div className="g_center h-75">
                        <h5>{t("No Data Available")}</h5>
                      </div>
                    )}
                  </div>
                ))}

                <div className="d-grid gap-3" style={{ minWidth: 0 }}>
                  <div
                    className="rounded-4 p-2"
                    style={{
                      border: "1px solid #DDE3EE",
                      overflow: "hidden",
                      wordWrap: "break-word",
                    }}
                  >
                    <SaudiNonSaudiSummaryCard
                      stats={dashboardData?.saudi_or_non_saudi_stats}
                    />
                  </div>
                  <div
                    className="rounded-4 p-2"
                    style={{
                      border: "1px solid #DDE3EE",
                      overflow: "hidden",
                      wordWrap: "break-word",
                    }}
                  >
                    <ExperienceSummaryCard
                      stats={dashboardData?.experienced_or_new_stats}
                    />
                  </div>
                </div>
              </div>
            </Col>
            <Col sm={12}>
              <Card>
                <div
                  style={{ border: "1px solid #DDE3EE" }}
                  className="rounded-4 px-2 py-2"
                >
                  <div className="d-flex align-items-center gap-2 my-2">
                    <IconifyIcon
                      icon="solar:user-outline"
                      width={24}
                      height={24}
                      style={{
                        color:
                          layoutMode === layoutModeTypes.LIGHT
                            ? Colors.primary
                            : Colors.primaryLight,
                      }}
                    />
                    <CardTitle
                      style={{
                        color:
                          layoutMode === layoutModeTypes.LIGHT
                            ? Colors.black
                            : Colors.white,
                      }}
                      className="m-0"
                    >
                      {t("Age")}
                    </CardTitle>
                  </div>
                  <BarChart data={data} options={options} height={300} />
                </div>
              </Card>
            </Col>
          </Row>
          <CardBody
            className="rounded-4"
            style={{ border: "1px solid #DDE3EE" }}
          >
            <PracticalTrainingGoalsDashboard
              dashboardData={dashboardData}
              filters={filters}
              layoutMode={layoutMode}
            />
          </CardBody>
        </>
      )}
    </>
  )
}

export default PracticalTrainingStatistics
